import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SupplierPicker from './SupplierPicker';
import SeasonPicker from './SeasonPicker';
import {
  createAttributeValue,
  createPurchaseOrderFromImport,
  fetchAttributes,
  fetchNextAttributeNumber,
  fetchSuppliers,
  fetchSupplierBrands,
  parsePurchaseOrderImportExcel,
  validatePurchaseOrderImport,
} from '../../services/purchasingService';
import {
  AttributeType,
  PurchaseOrder,
  PurchaseOrderImportDecisions,
  PurchaseOrderImportMapping,
  PurchaseOrderImportMappingField,
  PurchaseOrderImportMappingValue,
  PurchaseOrderImportOptions,
  PurchaseOrderImportWeightMode,
  PurchaseOrderImportParseResponse,
  PurchaseOrderImportValidateResponse,
  BrandOption,
  SupplierOption,
} from '../../types/purchasing';

type StepId = 'supplier' | 'file' | 'mapping' | 'validate';
type AttributeCodeOptions = Record<AttributeType, string[]>;

const ATTRIBUTE_TYPE_LABELS: Record<AttributeType, string> = {
  attribute1: 'Färg',
  attribute2: 'Storlek',
  attribute3: 'Längd',
};

const ATTRIBUTE_CODE_MAX_LENGTH = 255;

function canCreatePurchaseOrderImport(
  blockingIssues: PurchaseOrderImportValidateResponse['blockingIssues'],
): boolean {
  return (
    blockingIssues.missingBrands.length === 0 &&
    blockingIssues.missingAttributes.length === 0 &&
    blockingIssues.inconsistentEanRows.length === 0 &&
    blockingIssues.unresolvedEanMatches.length === 0 &&
    blockingIssues.unresolvedArticleNumberConflicts.length === 0 &&
    blockingIssues.styleOverrideConflicts.length === 0 &&
    blockingIssues.pluConflicts.length === 0 &&
    blockingIssues.attributeCodesTooLong.length === 0 &&
    blockingIssues.truncatedAttributeMatches.length === 0
  );
}

const INTERNAL_FIELDS: Array<{
  field: PurchaseOrderImportMappingField;
  label: string;
}> = [
  { field: 'articleNumber', label: 'Artikelnummer' },
  { field: 'brandName', label: 'Varumärke' },
  { field: 'productName', label: 'Produktnamn' },
  { field: 'supplierOrderNumber', label: 'Lev. ordernr' },
  { field: 'notes', label: 'Kommentar' },
  { field: 'attribute1Code', label: 'Färg' },
  { field: 'attribute2Code', label: 'Storlek' },
  { field: 'attribute3Code', label: 'Längd' },
  { field: 'priceOrig', label: 'Försäljningspris (RRP)' },
  { field: 'campaignPrice', label: 'Kampanjpris' },
  { field: 'supplierPrice', label: 'Inköpspris' },
  { field: 'priceOrigDkk', label: 'Ord. pris DKK' },
  { field: 'priceDkk', label: 'Pris DKK' },
  { field: 'deliveryDate', label: 'Leveransdatum' },
  { field: 'ean13', label: 'EAN' },
  { field: 'quantity', label: 'Antal' },
  { field: 'weight', label: 'Vikt' },
];

function AttrChips(props: {
  a1: string | null | undefined;
  a2: string | null | undefined;
  a3: string | null | undefined;
}) {
  const items = [
    { label: props.a1, key: 'a1' },
    { label: props.a2, key: 'a2' },
    { label: props.a3, key: 'a3' },
  ].filter((i) => i.label && String(i.label).trim());

  if (!items.length) return <Chip size="small" label="—" variant="outlined" />;

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {items.map((i) => (
        <Chip
          key={i.key}
          size="small"
          label={String(i.label)}
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

function DraggableChip(props: { id: string; label: string; used?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: props.id });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    border: props.used
      ? '1px solid rgba(46,125,50,0.55)'
      : '1px solid rgba(0,0,0,0.12)',
    borderRadius: 12,
    padding: '4px 8px',
    background: props.used ? 'rgba(46,125,50,0.10)' : 'rgba(255,255,255,0.9)',
    color: props.used ? '#1B5E20' : undefined,
    cursor: 'grab',
    fontSize: 12,
    lineHeight: 1.4,
    userSelect: 'none',
  };
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {props.label}
    </div>
  );
}

function UsedByBadge(props: { usedBy: string[] }) {
  if (!props.usedBy.length) return null;
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', maxWidth: 160, lineHeight: 1.2 }}
    >
      {props.usedBy.join(', ')}
    </Typography>
  );
}

function mappingValueToColumns(
  value: PurchaseOrderImportMappingValue | undefined,
): string[] {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

function hasMappingValue(
  value: PurchaseOrderImportMappingValue | undefined,
): boolean {
  return mappingValueToColumns(value).length > 0;
}

function DroppableField(props: {
  id: string;
  label: string;
  value: PurchaseOrderImportMappingValue | undefined;
  onClear: () => void;
  helperText?: string;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: props.id });
  const columns = mappingValueToColumns(props.value);
  const hasValue = columns.length > 0;
  return (
    <Box
      ref={setNodeRef}
      sx={{
        border: '1px dashed',
        borderColor: hasValue
          ? 'success.main'
          : isOver
            ? 'primary.main'
            : 'divider',
        borderRadius: 1,
        p: 0.75,
        minHeight: 46,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.75,
        bgcolor: hasValue
          ? 'rgba(46,125,50,0.08)'
          : isOver
            ? 'action.hover'
            : 'background.paper',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {props.label}
        </Typography>
        {columns.length ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.25, gap: 0.5 }}
          >
            {columns.map((column, index) => (
              <Chip
                key={`${column}:${index}`}
                size="small"
                label={column}
                color="success"
                variant="outlined"
                sx={{ maxWidth: 160 }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            Släpp kolumn
          </Typography>
        )}
        {props.helperText ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.2 }}
          >
            {props.helperText}
          </Typography>
        ) : null}
      </Box>
      {columns.length ? (
        <Button
          size="small"
          variant="text"
          onClick={props.onClear}
          sx={{ minWidth: 0, px: 0.5 }}
        >
          Rensa
        </Button>
      ) : null}
    </Box>
  );
}

function PluImportOptionsControl(props: {
  columns: string[];
  enabled: boolean;
  value: PurchaseOrderImportMappingValue | undefined;
  onEnabledChange: (enabled: boolean) => void;
  onColumnChange: (column: string) => void;
}) {
  const selectedColumns = mappingValueToColumns(props.value);
  const selectedColumn = selectedColumns[0] ?? '';
  const needsColumn = props.enabled && !selectedColumn;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: needsColumn ? 'warning.main' : 'divider',
        borderRadius: 1.5,
        p: 1.25,
        bgcolor: props.enabled ? 'rgba(25,118,210,0.04)' : 'background.paper',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={props.enabled}
              onChange={(event) => props.onEnabledChange(event.target.checked)}
            />
          }
          label="Importera PLU från Excel"
          sx={{ flex: 1, mr: 0 }}
        />
        <TextField
          select
          size="small"
          label="PLU-kolumn"
          value={selectedColumn}
          disabled={!props.enabled || props.columns.length === 0}
          onChange={(event) => props.onColumnChange(event.target.value)}
          error={needsColumn}
          helperText={
            props.enabled
              ? needsColumn
                ? 'Välj kolumnen som innehåller PLU.'
                : 'Används bara när EAN inte redan finns i systemet.'
              : 'PLU genereras senare om fältet lämnas avstängt.'
          }
          sx={{ minWidth: { sm: 280 } }}
        >
          {props.columns.map((column) => (
            <MenuItem key={column} value={column}>
              {column}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Box>
  );
}

function sizeRank(raw: string | null | undefined): number {
  const s = (raw || '').toString().trim().toUpperCase();
  if (!s) return 9999;

  // Common apparel sizing order.
  const order = [
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    'XXXL',
    '3XL',
    '4XL',
    '5XL',
    '6XL',
    '7XL',
    '8XL',
  ];
  const idx = order.indexOf(s);
  if (idx >= 0) return idx;

  // Numeric sizes: 28, 30, 32 etc.
  const n = Number(s.replace(',', '.'));
  if (Number.isFinite(n)) return 100 + n;

  return 5000;
}

function normalizeForMatch(raw: string): string {
  return (raw || '')
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[\s\-_]+/g, '');
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const v0 = new Array<number>(b.length + 1);
  const v1 = new Array<number>(b.length + 1);
  for (let i = 0; i <= b.length; i++) v0[i] = i;
  for (let i = 0; i < a.length; i++) {
    v1[0] = i + 1;
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
    }
    for (let j = 0; j <= b.length; j++) v0[j] = v1[j];
  }
  return v0[b.length];
}

function suggestSimilarCodes(
  missingCode: string,
  candidates: string[],
): string[] {
  const q = normalizeForMatch(missingCode);
  if (!q) return [];
  const scored = candidates
    .map((c) => {
      const n = normalizeForMatch(c);
      let score = 0;
      if (n === q) score += 10_000;
      if (n.startsWith(q) || q.startsWith(n)) score += 4_000;
      if (n.includes(q) || q.includes(n)) score += 2_000;
      score += Math.max(0, 250 - levenshtein(q, n));
      return { c, score };
    })
    .sort((a, b) => b.score - a.score || a.c.localeCompare(b.c, 'sv'));

  return scored.slice(0, 5).map((s) => s.c);
}

function AddMissingAttributeDialog(props: {
  open: boolean;
  attributeType: AttributeType | null;
  missingCode: string;
  onClose: () => void;
  onAdd: (attributeCode: string, attributeNumber: string) => Promise<void>;
}) {
  const [attributeCode, setAttributeCode] = useState('');
  const [attributeNumber, setAttributeNumber] = useState('');
  const [loadingNumber, setLoadingNumber] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open || !props.attributeType) return;

    let cancelled = false;
    setAttributeCode(props.missingCode);
    setAttributeNumber('');
    setError(null);
    setSubmitting(false);

    if (props.attributeType !== 'attribute1') {
      setLoadingNumber(false);
      return () => {
        cancelled = true;
      };
    }

    setLoadingNumber(true);
    fetchNextAttributeNumber()
      .then((nextNumber) => {
        if (!cancelled) setAttributeNumber(nextNumber);
      })
      .catch((e) => {
        console.error('Failed to load next attribute number:', e);
        if (!cancelled) {
          setError('Kunde inte hämta nästa attributnummer.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingNumber(false);
      });

    return () => {
      cancelled = true;
    };
  }, [props.open, props.attributeType, props.missingCode]);

  const handleSubmit = async () => {
    const nextCode = attributeCode.trim();
    const nextNumber = attributeNumber.trim();
    if (!nextCode || !nextNumber) return;

    try {
      setSubmitting(true);
      setError(null);
      await props.onAdd(nextCode, nextNumber);
      props.onClose();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response
        ?.data?.error;
      setError(msg || 'Kunde inte lägga till attributet.');
    } finally {
      setSubmitting(false);
    }
  };

  const label = props.attributeType
    ? ATTRIBUTE_TYPE_LABELS[props.attributeType]
    : 'Attribut';
  const isColor = props.attributeType === 'attribute1';

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="xs">
      <DialogTitle>Lägg till {label.toLowerCase()}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Attributtyp" value={label} fullWidth disabled />
          <TextField
            label="Attributkod"
            value={attributeCode}
            onChange={(e) => setAttributeCode(e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: ATTRIBUTE_CODE_MAX_LENGTH }}
            helperText={`${attributeCode.length}/${ATTRIBUTE_CODE_MAX_LENGTH} tecken`}
          />
          <TextField
            label="Attributnummer"
            value={attributeNumber}
            onChange={(e) => setAttributeNumber(e.target.value)}
            fullWidth
            required
            disabled={isColor || loadingNumber}
            helperText={
              isColor
                ? 'Färgnummer räknas ut automatiskt.'
                : 'Ange nummer manuellt för storlek/längd.'
            }
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={submitting}>
          Avbryt
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting ||
            loadingNumber ||
            !attributeCode.trim() ||
            !attributeNumber.trim() ||
            attributeCode.trim().length > ATTRIBUTE_CODE_MAX_LENGTH
          }
        >
          {submitting ? 'Lägger till…' : 'Lägg till'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PurchaseOrderExcelImportDialog(props: {
  open: boolean;
  onClose: () => void;
  onCreated: (order: PurchaseOrder) => void;
}) {
  const [step, setStep] = useState<StepId>('supplier');
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [season, setSeason] = useState<string | null>(null);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierBrands, setSupplierBrands] = useState<BrandOption[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] =
    useState<PurchaseOrderImportParseResponse | null>(null);
  const [mapping, setMapping] = useState<PurchaseOrderImportMapping>({});
  const [weightMode, setWeightMode] =
    useState<PurchaseOrderImportWeightMode>('gramNumber');
  const [importPluFromExcel, setImportPluFromExcel] = useState(false);
  const [missingProductsOnly, setMissingProductsOnly] = useState(false);
  const [activeDragColumn, setActiveDragColumn] = useState<string | null>(null);
  const [brandSource, setBrandSource] = useState<'excel' | 'manual'>('excel');
  const [manualBrandName, setManualBrandName] = useState('');

  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] =
    useState<PurchaseOrderImportValidateResponse | null>(null);
  const [decisions, setDecisions] = useState<PurchaseOrderImportDecisions>({});
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [attributes, setAttributes] = useState<AttributeCodeOptions | null>(
    null,
  );
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributeToAdd, setAttributeToAdd] = useState<{
    attributeType: AttributeType;
    missingCode: string;
  } | null>(null);
  const internalFieldsScrollRef = useRef<HTMLDivElement | null>(null);
  const dragPointerRef = useRef<{ x: number; y: number } | null>(null);
  const scrollFrameRef = useRef<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const availableColumns = useMemo(
    () => parseResult?.columns ?? [],
    [parseResult],
  );
  const selectedSupplier = suppliers.find(
    (supplier) => supplier.supplierId === supplierId,
  );
  const isBestsellerSupplier = Boolean(
    selectedSupplier?.supplierName.toLowerCase().includes('bestseller'),
  );
  const supplierBrandNames = useMemo(
    () =>
      Array.from(
        new Set(
          supplierBrands.map((brand) => brand.brandName.trim()).filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b, 'sv')),
    [supplierBrands],
  );

  const internalFieldLabelByField = useMemo(() => {
    const map = new Map<PurchaseOrderImportMappingField, string>();
    INTERNAL_FIELDS.forEach(({ field, label }) => map.set(field, label));
    map.set('pluNo', 'PLU');
    return map;
  }, []);

  const usedByForColumn = useMemo(() => {
    const used = new Map<string, string[]>();
    (Object.keys(mapping) as PurchaseOrderImportMappingField[]).forEach(
      (field) => {
        if (field === 'pluNo' && !importPluFromExcel) return;
        const label = internalFieldLabelByField.get(field) ?? field;
        mappingValueToColumns(mapping[field]).forEach((col) => {
          const list = used.get(col) ?? [];
          list.push(label);
          used.set(col, list);
        });
      },
    );
    // stable order for display
    for (const [key, list] of Array.from(used.entries())) {
      used.set(
        key,
        [...list].sort((a, b) => a.localeCompare(b, 'sv')),
      );
    }
    return used;
  }, [mapping, internalFieldLabelByField, importPluFromExcel]);
  const detectedTemplate = parseResult?.detectedTemplate ?? null;
  const detectedTemplateLabel =
    detectedTemplate === 'bestseller'
      ? 'Bestseller'
      : detectedTemplate === 'sports_group_denmark'
        ? 'Sports Group Denmark'
        : null;

  const needsManualMapping = detectedTemplate == null;
  const importOptions = useMemo<PurchaseOrderImportOptions>(
    () => ({
      ...(needsManualMapping ? { weightMode } : {}),
      ...(importPluFromExcel ? { importPluNo: true } : {}),
      ...(isBestsellerSupplier && missingProductsOnly
        ? { missingProductsOnly: true }
        : {}),
    }),
    [
      needsManualMapping,
      weightMode,
      importPluFromExcel,
      detectedTemplate,
      missingProductsOnly,
      isBestsellerSupplier,
    ],
  );
  const requestMapping = useMemo<PurchaseOrderImportMapping | undefined>(() => {
    const next: PurchaseOrderImportMapping = needsManualMapping
      ? { ...mapping }
      : {};
    if (importPluFromExcel && hasMappingValue(mapping.pluNo)) {
      next.pluNo = mapping.pluNo;
    } else {
      delete next.pluNo;
    }
    return Object.values(next).some((value) => hasMappingValue(value))
      ? next
      : undefined;
  }, [needsManualMapping, mapping, importPluFromExcel]);
  const pluImportNeedsColumn =
    importPluFromExcel && !hasMappingValue(mapping.pluNo);

  const canRunValidate = Boolean(supplierId && file && !pluImportNeedsColumn);
  const canCreate = Boolean(
    validateResult?.canCreate &&
      supplierId &&
      file &&
      !pluImportNeedsColumn &&
      !missingProductsOnly,
  );
  const missingProducts = validateResult?.missingProducts ?? [];
  const canExportMissingProducts = Boolean(
    missingProductsOnly &&
      validateResult?.canCreate &&
      missingProducts.length > 0 &&
      !pluImportNeedsColumn,
  );
  const missingBrands = validateResult?.blockingIssues.missingBrands ?? [];
  const hasMissingBrands = missingBrands.length > 0;
  const hasMissingAttributes = Boolean(
    validateResult?.blockingIssues.missingAttributes.length,
  );
  const inconsistentEanRows =
    validateResult?.blockingIssues.inconsistentEanRows ?? [];
  const hasInconsistentEans = inconsistentEanRows.length > 0;
  const styleOverrideConflicts =
    validateResult?.blockingIssues.styleOverrideConflicts ?? [];
  const hasStyleOverrideConflicts = styleOverrideConflicts.length > 0;
  const pluConflicts = validateResult?.blockingIssues.pluConflicts ?? [];
  const hasPluConflicts = pluConflicts.length > 0;
  const attributeCodesTooLong =
    validateResult?.blockingIssues.attributeCodesTooLong ?? [];
  const hasAttributeCodesTooLong = attributeCodesTooLong.length > 0;
  const truncatedAttributeMatches =
    validateResult?.blockingIssues.truncatedAttributeMatches ?? [];
  const hasTruncatedAttributeMatches = truncatedAttributeMatches.length > 0;
  const hasBlockingIssues =
    hasMissingBrands ||
    hasMissingAttributes ||
    hasInconsistentEans ||
    hasStyleOverrideConflicts ||
    hasPluConflicts ||
    hasAttributeCodesTooLong ||
    hasTruncatedAttributeMatches;

  const sortedEanMatches = useMemo(() => {
    const list = validateResult?.eanMatches ?? [];
    return [...list].sort((a, b) => {
      const prodA = a.system.productNumber || '';
      const prodB = b.system.productNumber || '';
      if (prodA !== prodB) return prodA.localeCompare(prodB, 'sv');

      const rA = sizeRank(a.system.attribute2Code ?? a.import.attribute2Code);
      const rB = sizeRank(b.system.attribute2Code ?? b.import.attribute2Code);
      if (rA !== rB) return rA - rB;

      return String(a.rowId).localeCompare(String(b.rowId), 'sv', {
        numeric: true,
      });
    });
  }, [validateResult?.eanMatches]);

  const sortedVariantMatches = useMemo(() => {
    const list = validateResult?.variantMatches ?? [];
    return [...list].sort((a, b) => {
      const prodA = a.system.productNumber || '';
      const prodB = b.system.productNumber || '';
      if (prodA !== prodB) return prodA.localeCompare(prodB, 'sv');

      const rA = sizeRank(a.system.attribute2Code ?? a.import.attribute2Code);
      const rB = sizeRank(b.system.attribute2Code ?? b.import.attribute2Code);
      if (rA !== rB) return rA - rB;

      return String(a.rowId).localeCompare(String(b.rowId), 'sv', {
        numeric: true,
      });
    });
  }, [validateResult?.variantMatches]);

  useEffect(() => {
    if (!props.open) return;
    setLoadingSuppliers(true);
    fetchSuppliers()
      .then(setSuppliers)
      .catch((e) => console.error('Failed to load suppliers:', e))
      .finally(() => setLoadingSuppliers(false));
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    if (!supplierId) {
      setSupplierBrands([]);
      return;
    }
    setLoadingBrands(true);
    fetchSupplierBrands(supplierId)
      .then(setSupplierBrands)
      .catch((e) => {
        console.error('Failed to load supplier brands:', e);
        setSupplierBrands([]);
      })
      .finally(() => setLoadingBrands(false));
  }, [props.open, supplierId]);

  useEffect(() => {
    if (!props.open) return;
    // Reset when reopened
    setStep('supplier');
    setSupplierId(null);
    setSeason(null);
    setFile(null);
    setParseResult(null);
    setMapping({});
    setWeightMode('gramNumber');
    setImportPluFromExcel(false);
    setMissingProductsOnly(false);
    setBrandSource('excel');
    setManualBrandName('');
    setSupplierBrands([]);
    setLoadingBrands(false);
    setValidateResult(null);
    setDecisions({});
    setErrorMessage(null);
    setParsing(false);
    setValidating(false);
    setCreating(false);
    setAttributeToAdd(null);
  }, [props.open]);

  const handleParse = async () => {
    if (!file || !supplierId) return;
    setParsing(true);
    try {
      setErrorMessage(null);
      const parsed = await parsePurchaseOrderImportExcel(file, supplierId);
      setParseResult(parsed);
      if (parsed.detectedTemplate !== 'bestseller') {
        setMissingProductsOnly(false);
      }
      setStep(parsed.detectedTemplate ? 'validate' : 'mapping');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response
        ?.data?.error;
      setErrorMessage(msg || 'Kunde inte läsa Excel-filen.');
      console.error('Parse failed:', e);
    } finally {
      setParsing(false);
    }
  };

  const handleValidate = async () => {
    if (!supplierId || !file) return;
    setValidating(true);
    try {
      setErrorMessage(null);
      const defaults =
        brandSource === 'manual' && manualBrandName.trim()
          ? { brandName: manualBrandName.trim() }
          : undefined;
      const result = await validatePurchaseOrderImport({
        supplierId,
        file,
        mapping: requestMapping,
        decisions,
        defaults,
        options: importOptions,
      });
      setValidateResult(result);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response
        ?.data?.error;
      setErrorMessage(msg || 'Kontroll misslyckades.');
      console.error('Validate failed:', e);
    } finally {
      setValidating(false);
    }
  };

  const loadAttributeOptions = useCallback(async () => {
    setLoadingAttributes(true);
    try {
      const res = await fetchAttributes();
      setAttributes({
        attribute1: (res.attribute1 || []).map((a) => a.code).filter(Boolean),
        attribute2: (res.attribute2 || []).map((a) => a.code).filter(Boolean),
        attribute3: (res.attribute3 || []).map((a) => a.code).filter(Boolean),
      });
    } catch (e) {
      console.error('Failed to load attributes:', e);
      setAttributes({ attribute1: [], attribute2: [], attribute3: [] });
    } finally {
      setLoadingAttributes(false);
    }
  }, []);

  useEffect(() => {
    if (step !== 'validate') return;
    if (
      !hasMissingAttributes &&
      !hasAttributeCodesTooLong &&
      !hasTruncatedAttributeMatches
    ) {
      return;
    }
    if (attributes || loadingAttributes) return;
    void loadAttributeOptions();
  }, [
    step,
    hasMissingAttributes,
    hasAttributeCodesTooLong,
    hasTruncatedAttributeMatches,
    attributes,
    loadingAttributes,
    loadAttributeOptions,
  ]);

  useEffect(() => {
    if (step !== 'validate') return;
    if (!canRunValidate) return;
    void handleValidate();
  }, [step, supplierId, file, canRunValidate]);

  useEffect(() => {
    if (step !== 'validate') return;
    if (!canRunValidate) return;
    // Re-run validate when decisions change
    void handleValidate();
  }, [
    decisions,
    requestMapping,
    brandSource,
    manualBrandName,
    importOptions,
    canRunValidate,
  ]);

  useEffect(() => {
    if (!activeDragColumn) return;

    const onPointerMove = (event: PointerEvent) => {
      dragPointerRef.current = { x: event.clientX, y: event.clientY };
    };

    const tick = () => {
      const pointer = dragPointerRef.current;
      const scrollElement = internalFieldsScrollRef.current;

      if (pointer && scrollElement) {
        const rect = scrollElement.getBoundingClientRect();
        const insideX = pointer.x >= rect.left && pointer.x <= rect.right;
        const insideY = pointer.y >= rect.top && pointer.y <= rect.bottom;

        if (insideX && insideY) {
          const threshold = 56;
          const topDistance = pointer.y - rect.top;
          const bottomDistance = rect.bottom - pointer.y;
          let delta = 0;

          if (topDistance < threshold) {
            delta = -Math.ceil(((threshold - topDistance) / threshold) * 18);
          } else if (bottomDistance < threshold) {
            delta = Math.ceil(((threshold - bottomDistance) / threshold) * 18);
          }

          if (delta !== 0) scrollElement.scrollTop += delta;
        }
      }

      scrollFrameRef.current = window.requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    scrollFrameRef.current = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (scrollFrameRef.current != null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
      scrollFrameRef.current = null;
      dragPointerRef.current = null;
    };
  }, [activeDragColumn]);

  const handleDragStart = (event: DragStartEvent) => {
    const column = String(event.active.id);
    setActiveDragColumn(column);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragColumn(null);
    const { active, over } = event;
    if (!over) return;
    const column = String(active.id);
    const field = String(over.id).replace(
      /^field:/,
      '',
    ) as PurchaseOrderImportMappingField;
    if (!INTERNAL_FIELDS.some((f) => f.field === field)) return;
    setMapping((prev) => {
      if (field !== 'attribute1Code') {
        return { ...prev, [field]: column };
      }

      const current = mappingValueToColumns(prev.attribute1Code);
      return {
        ...prev,
        attribute1Code: current.includes(column)
          ? current
          : [...current, column],
      };
    });
  };

  const handleDragCancel = () => {
    setActiveDragColumn(null);
  };

  const handlePluImportEnabledChange = (enabled: boolean) => {
    setImportPluFromExcel(enabled);
    setValidateResult(null);
    setErrorMessage(null);
  };

  const handlePluColumnChange = (column: string) => {
    setMapping((prev) => ({ ...prev, pluNo: column || undefined }));
    setValidateResult(null);
    setErrorMessage(null);
  };

  const handleMissingProductsOnlyChange = (enabled: boolean) => {
    setMissingProductsOnly(enabled);
    setValidateResult(null);
    setErrorMessage(null);
  };

  const handleAddMissingAttribute = async (
    attributeType: AttributeType,
    missingCode: string,
    attributeCode: string,
    attributeNumber: string,
  ) => {
    setErrorMessage(null);
    await createAttributeValue({
      attributeType,
      attributeCode,
      attributeNumber,
    });

    const existingMapping =
      decisions.attributeMappings?.[attributeType]?.[missingCode];
    const isDirectAddition =
      attributeCode.trim() === missingCode.trim() &&
      existingMapping === undefined;

    if (attributes) {
      setAttributes((prev) => {
        if (!prev || prev[attributeType].includes(attributeCode)) return prev;
        return {
          ...prev,
          [attributeType]: [...prev[attributeType], attributeCode],
        };
      });
    } else {
      await loadAttributeOptions();
    }

    if (isDirectAddition) {
      // Adding the exact missing code changes only the catalog validation. The
      // parsed rows, EAN matches and all other validation results are unchanged,
      // so avoid uploading and parsing the full import again.
      setValidateResult((prev) => {
        if (!prev) return prev;
        const missingAttributes = prev.blockingIssues.missingAttributes
          .map((group) =>
            group.attributeType === attributeType
              ? {
                  ...group,
                  missing: group.missing.filter((code) => code !== missingCode),
                }
              : group,
          )
          .filter((group) => group.missing.length > 0);
        const blockingIssues = {
          ...prev.blockingIssues,
          missingAttributes,
        };
        return {
          ...prev,
          blockingIssues,
          canCreate: canCreatePurchaseOrderImport(blockingIssues),
        };
      });
      return;
    }

    // A different code is a mapping decision and changes the imported rows;
    // retain the full validation path for that case.
    setDecisions((prev) => {
      const nextTypeMap = {
        ...(prev.attributeMappings?.[attributeType] || {}),
      };

      if (attributeCode === missingCode) {
        delete nextTypeMap[missingCode];
      } else {
        nextTypeMap[missingCode] = attributeCode;
      }

      return {
        ...prev,
        attributeMappings: {
          ...(prev.attributeMappings || {}),
          [attributeType]: nextTypeMap,
        },
      };
    });
  };

  const handleCreate = async () => {
    if (!supplierId || !file || missingProductsOnly) return;
    setCreating(true);
    try {
      setErrorMessage(null);
      const defaults =
        brandSource === 'manual' && manualBrandName.trim()
          ? { brandName: manualBrandName.trim() }
          : undefined;
      const order = await createPurchaseOrderFromImport({
        supplierId,
        file,
        mapping: requestMapping,
        season,
        decisions,
        defaults,
        options: importOptions,
      });
      props.onCreated(order);
      props.onClose();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response
        ?.data?.error;
      setErrorMessage(msg || 'Kunde inte skapa inköpsorder från importen.');
      console.error('Create from import failed:', e);
    } finally {
      setCreating(false);
    }
  };

  const handleExportMissingProducts = () => {
    if (!canExportMissingProducts) return;
    const headers = [
      'Rad',
      'Artikelnummer',
      'Produktnamn',
      'Style Variant Name',
      'Brand',
      'Sub-brand',
      'Färg',
      'Storlek',
      'Längd',
      'EAN',
      'Antal köpt',
      'Inköpspris',
      'Rek pris SEK',
      'Kampanjpris',
      'Rek pris DKK',
      'Pris DKK',
      'Vikt gram',
      'Leveransmånad',
      'Supplier order no',
      'Kommentar',
      'PLU',
      'Artikel finns i Mirakel',
      'Variant finns i Mirakel',
    ];
    const csvCell = (value: unknown) => {
      let text = value == null ? '' : String(value);
      if (typeof value === 'string' && /^[\s]*[=+@-]/.test(text)) {
        text = `'${text}`;
      }
      return `"${text.replace(/"/g, '""')}"`;
    };
    const rows = missingProducts.map((row) => [
      row.rowId,
      row.articleNumber,
      row.productName,
      row.styleVariantName,
      row.bestsellerBrandName || row.brandName,
      row.bestsellerSubBrandName,
      row.attribute1Code,
      row.attribute2Code,
      row.attribute3Code,
      row.ean13,
      row.quantity,
      row.supplierPrice,
      row.priceOrig,
      row.campaignPrice,
      row.priceOrigDkk,
      row.priceDkk,
      row.weight,
      row.deliveryMonth,
      row.supplierOrderNumber,
      row.notes,
      row.pluNo,
      row.productExistsByArticleNumber ? 'Ja' : 'Nej',
      row.variantExistsByAttributes ? 'Ja' : 'Nej',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(';'))
      .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bestseller-saknade-ean-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="lg">
        <DialogTitle>Importera inköpsorder från Excel</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {errorMessage ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : null}
            {step === 'supplier' ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Välj leverantör först. Därefter laddar du upp Excel-filen.
                </Typography>
                {loadingSuppliers ? <CircularProgress size={24} /> : null}
                <SupplierPicker
                  suppliers={suppliers}
                  value={supplierId}
                  onChange={setSupplierId}
                />
                {isBestsellerSupplier ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: missingProductsOnly
                        ? 'info.main'
                        : 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                      bgcolor: missingProductsOnly
                        ? 'rgba(2,136,209,0.04)'
                        : 'background.paper',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={missingProductsOnly}
                          onChange={(event) =>
                            handleMissingProductsOnlyChange(
                              event.target.checked,
                            )
                          }
                        />
                      }
                      label="Saknade produkter"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Ta ut rader där EAN saknas i Mirakel. Inget inköp skapas.
                    </Typography>
                  </Box>
                ) : null}
                <SeasonPicker
                  value={season}
                  onChange={(code) => setSeason(code)}
                />
                <Button
                  variant="contained"
                  disabled={!supplierId}
                  onClick={() => setStep('file')}
                >
                  Nästa
                </Button>
              </>
            ) : null}

            {step === 'file' ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Ladda upp Excel-filen. Vi läser först kolumnerna och avgör om
                  vi kan auto-mappa.
                </Typography>
                <TextField
                  type="file"
                  inputProps={{ accept: '.xlsx,.xlsm,.csv' }}
                  onChange={(e) => {
                    const next =
                      (e.currentTarget as HTMLInputElement).files?.[0] ?? null;
                    setFile(next);
                    setParseResult(null);
                    setValidateResult(null);
                    setDecisions({});
                    setImportPluFromExcel(false);
                    setMapping({});
                    setAttributeToAdd(null);
                  }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => setStep('supplier')}
                  >
                    Tillbaka
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!file || parsing}
                    onClick={handleParse}
                  >
                    {parsing ? 'Läser…' : 'Fortsätt'}
                  </Button>
                </Stack>
              </>
            ) : null}

            {step === 'mapping' ? (
              <>
                <Alert severity="info">
                  Ingen färdig mall hittades för leverantören i filen. Mappa
                  kolumner manuellt (dra och släpp).
                </Alert>
                <DndContext
                  sensors={sensors}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                  autoScroll={false}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <Box sx={{ flex: 0.9, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Excel-kolumner
                      </Typography>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={0.75}
                        sx={{
                          maxHeight: { md: 520 },
                          overflow: 'auto',
                          pr: 0.5,
                        }}
                      >
                        {availableColumns.map((c) => {
                          const usedBy = usedByForColumn.get(c) ?? [];
                          return (
                            <Box
                              key={c}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                              }}
                            >
                              <DraggableChip
                                id={c}
                                label={c}
                                used={usedBy.length > 0}
                              />
                              <UsedByBadge usedBy={usedBy} />
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                    <Box sx={{ flex: 1.4, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Interna fält
                      </Typography>
                      <Box
                        ref={internalFieldsScrollRef}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                          },
                          gap: 0.75,
                          maxHeight: { md: 520 },
                          overflow: 'auto',
                          pr: 0.5,
                        }}
                      >
                        {INTERNAL_FIELDS.map(({ field, label }) => {
                          if (field === 'brandName') {
                            const hasExcelBrand = hasMappingValue(
                              mapping.brandName,
                            );
                            const showManual = brandSource === 'manual';
                            return (
                              <Box
                                key={field}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  p: 0.75,
                                }}
                              >
                                <Stack spacing={0.75}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {label}
                                    </Typography>

                                    <RadioGroup
                                      row
                                      value={brandSource}
                                      onChange={(e) => {
                                        const next = e.target.value as
                                          | 'excel'
                                          | 'manual';
                                        setBrandSource(next);
                                        if (next === 'manual') {
                                          setMapping((prev) => ({
                                            ...prev,
                                            brandName: undefined,
                                          }));
                                        } else {
                                          setManualBrandName('');
                                        }
                                      }}
                                      sx={{
                                        '& .MuiFormControlLabel-root': {
                                          mr: 0.5,
                                        },
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: 12,
                                        },
                                      }}
                                    >
                                      <FormControlLabel
                                        value="excel"
                                        control={<Radio size="small" />}
                                        label="Excel"
                                      />
                                      <FormControlLabel
                                        value="manual"
                                        control={<Radio size="small" />}
                                        label="Manuellt"
                                      />
                                    </RadioGroup>
                                  </Stack>

                                  {brandSource === 'excel' ? (
                                    <DroppableField
                                      id={`field:${field}`}
                                      label="Excel-kolumn"
                                      value={mapping[field]}
                                      onClear={() =>
                                        setMapping((prev) => ({
                                          ...prev,
                                          [field]: undefined,
                                        }))
                                      }
                                    />
                                  ) : (
                                    <TextField
                                      select
                                      size="small"
                                      label="Välj varumärke"
                                      value={manualBrandName}
                                      onChange={(e) =>
                                        setManualBrandName(e.target.value)
                                      }
                                      disabled={
                                        loadingBrands ||
                                        supplierBrands.length === 0
                                      }
                                      helperText={
                                        loadingBrands
                                          ? 'Laddar varumärken…'
                                          : supplierBrands.length === 0
                                            ? 'Inga varumärken hittades för leverantören.'
                                            : 'Gäller alla rader i importen.'
                                      }
                                    >
                                      {supplierBrands.map((b) => (
                                        <MenuItem
                                          key={b.brandId}
                                          value={b.brandName}
                                        >
                                          {b.brandName}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}

                                  {brandSource === 'excel' && !hasExcelBrand ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Tips: om filen saknar varumärkeskolumn kan
                                      du välja “Manuellt”.
                                    </Typography>
                                  ) : null}

                                  {showManual && manualBrandName ? (
                                    <Chip
                                      size="small"
                                      label={`Manuellt: ${manualBrandName}`}
                                      variant="outlined"
                                    />
                                  ) : null}
                                </Stack>
                              </Box>
                            );
                          }

                          return (
                            <DroppableField
                              key={field}
                              id={`field:${field}`}
                              label={label}
                              value={mapping[field]}
                              helperText={
                                field === 'attribute1Code'
                                  ? 'Dra flera kolumner hit för t.ex. Färgkod - Färgnamn.'
                                  : undefined
                              }
                              onClear={() =>
                                setMapping((prev) => ({
                                  ...prev,
                                  [field]: undefined,
                                }))
                              }
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </Stack>
                  <DragOverlay dropAnimation={null}>
                    {activeDragColumn ? (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'primary.main',
                          borderRadius: 1.5,
                          px: 1.25,
                          py: 0.75,
                          bgcolor: 'background.paper',
                          boxShadow: 6,
                          color: 'primary.main',
                          fontSize: 12,
                          lineHeight: 1.4,
                          pointerEvents: 'none',
                        }}
                      >
                        {activeDragColumn}
                      </Box>
                    ) : null}
                  </DragOverlay>
                </DndContext>
                <PluImportOptionsControl
                  columns={availableColumns}
                  enabled={importPluFromExcel}
                  value={mapping.pluNo}
                  onEnabledChange={handlePluImportEnabledChange}
                  onColumnChange={handlePluColumnChange}
                />
                {hasMappingValue(mapping.weight) ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <FormControl
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 0.5, sm: 1.5 },
                      }}
                    >
                      <FormLabel sx={{ fontSize: 13 }}>Viktformat</FormLabel>
                      <RadioGroup
                        row
                        value={weightMode}
                        onChange={(event) =>
                          setWeightMode(
                            event.target.value as PurchaseOrderImportWeightMode,
                          )
                        }
                      >
                        <FormControlLabel
                          value="gramNumber"
                          control={<Radio size="small" />}
                          label="1 kg skrivs 1000"
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: 13,
                            },
                          }}
                        />
                        <FormControlLabel
                          value="kgDecimal"
                          control={<Radio size="small" />}
                          label="1 kg skrivs 1 eller 1,0"
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: 13,
                            },
                          }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                ) : null}
                <Divider />
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={() => setStep('file')}>
                    Tillbaka
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!canRunValidate}
                    onClick={() => setStep('validate')}
                  >
                    Kontroller
                  </Button>
                </Stack>
              </>
            ) : null}

            {step === 'validate' ? (
              <>
                {detectedTemplate ? (
                  <Alert severity="success">
                    Auto-mappning:{' '}
                    <b>{detectedTemplateLabel ?? detectedTemplate}</b>
                  </Alert>
                ) : null}
                <PluImportOptionsControl
                  columns={availableColumns}
                  enabled={importPluFromExcel}
                  value={mapping.pluNo}
                  onEnabledChange={handlePluImportEnabledChange}
                  onColumnChange={handlePluColumnChange}
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setStep(needsManualMapping ? 'mapping' : 'file')
                    }
                  >
                    Tillbaka
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!canRunValidate || validating}
                    onClick={handleValidate}
                  >
                    {validating ? 'Kontrollerar…' : 'Kör kontroller igen'}
                  </Button>
                  {validating ? <CircularProgress size={18} /> : null}
                </Stack>

                {missingProductsOnly && validateResult ? (
                  <Alert severity={missingProducts.length ? 'info' : 'success'}>
                    {missingProducts.length
                      ? `${missingProducts.length} EAN-rad(er) saknas i Mirakel. Utdraget skiljer på nya artiklar och artikel/variant-träffar där endast EAN saknas.`
                      : 'Alla giltiga EAN i filen matchar redan en SKU i Mirakel.'}
                    {!validateResult.canCreate
                      ? ' Lös blockerande kontroller ovan innan utdraget kan laddas ner.'
                      : ''}
                  </Alert>
                ) : null}

                {missingProductsOnly && missingProducts.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: 240,
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 1,
                    }}
                  >
                    <Stack spacing={0.75}>
                      {missingProducts.map((row) => (
                        <Box
                          key={row.rowId}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            pb: 0.75,
                          }}
                        >
                          <Typography variant="body2">
                            Rad {row.rowId}: {row.articleNumber ?? '—'} •{' '}
                            {row.productName ?? '—'} • EAN {row.ean13}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Artikel{' '}
                            {row.productExistsByArticleNumber
                              ? 'finns'
                              : 'saknas'}{' '}
                            i Mirakel • Variant{' '}
                            {row.variantExistsByAttributes ? 'finns' : 'saknas'}{' '}
                            på artikel/färg/storlek
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                {hasMissingBrands ? (
                  <Alert severity="error">
                    Vissa varumärken saknar supplier-mappning. Matcha dem mot
                    ett befintligt brand nedan innan importen kan slutföras.
                  </Alert>
                ) : null}

                {hasMissingBrands ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          Mappa saknade brands
                        </Typography>
                        {loadingBrands ? (
                          <Typography variant="caption" color="text.secondary">
                            Laddar brands…
                          </Typography>
                        ) : null}
                      </Stack>

                      {missingBrands.map((missingBrand) => {
                        const current =
                          decisions.brandMappings?.[missingBrand] ?? '';
                        return (
                          <Box
                            key={missingBrand}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              p: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 0.75 }}>
                              Saknas: <b>{missingBrand}</b>
                            </Typography>
                            <Autocomplete
                              options={supplierBrandNames}
                              value={current || null}
                              disabled={
                                loadingBrands || supplierBrandNames.length === 0
                              }
                              onChange={(_e, value) => {
                                setDecisions((prev) => {
                                  const nextBrandMappings = {
                                    ...(prev.brandMappings || {}),
                                  };
                                  if (value) {
                                    nextBrandMappings[missingBrand] = value;
                                  } else {
                                    delete nextBrandMappings[missingBrand];
                                  }
                                  return {
                                    ...prev,
                                    brandMappings: nextBrandMappings,
                                  };
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label="Matcha mot befintligt brand"
                                  helperText={
                                    supplierBrandNames.length > 0
                                      ? 'Importen använder det valda befintliga brandet.'
                                      : 'Inga brands finns registrerade för vald leverantör.'
                                  }
                                />
                              )}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}

                {hasAttributeCodesTooLong ? (
                  <Alert severity="error">
                    Vissa attributkoder i filen är längre än{' '}
                    {ATTRIBUTE_CODE_MAX_LENGTH} tecken och kan inte sparas.
                    Korta namnet i Excel-filen eller mappa till en befintlig kod
                    nedan.
                  </Alert>
                ) : null}

                {hasTruncatedAttributeMatches ? (
                  <Alert severity="error">
                    Vissa attributkoder är för långa och skulle kapas till en
                    befintlig kod i systemet. Mappa manuellt till rätt kod nedan
                    så att importen inte använder fel färg/storlek/längd.
                  </Alert>
                ) : null}

                {hasAttributeCodesTooLong || hasTruncatedAttributeMatches ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        Attribut med längdproblem
                      </Typography>
                      {attributeCodesTooLong.map((issue) => {
                        const type = issue.attributeType;
                        const label = ATTRIBUTE_TYPE_LABELS[type];
                        const candidates =
                          attributes?.[type]
                            ?.slice()
                            .sort((a, b) => a.localeCompare(b, 'sv')) ?? [];
                        const current =
                          decisions.attributeMappings?.[type]?.[issue.code] ??
                          '';
                        return (
                          <Box
                            key={`too-long:${type}:${issue.code}`}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              p: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 0.75 }}>
                              {label}: <b>{issue.code}</b> ({issue.code.length}{' '}
                              tecken, max {issue.maxLength})
                            </Typography>
                            <Autocomplete
                              options={candidates}
                              value={current || null}
                              disabled={loadingAttributes}
                              sx={{ minWidth: 220 }}
                              onChange={(_e, value) => {
                                const next = value || '';
                                setDecisions((prev) => ({
                                  ...prev,
                                  attributeMappings: {
                                    ...(prev.attributeMappings || {}),
                                    [type]: {
                                      ...(prev.attributeMappings?.[type] || {}),
                                      [issue.code]: next,
                                    },
                                  },
                                }));
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label="Ersätt med"
                                  helperText="Välj en kortare befintlig kod."
                                />
                              )}
                            />
                          </Box>
                        );
                      })}
                      {truncatedAttributeMatches.map((issue) => {
                        const type = issue.attributeType;
                        const label = ATTRIBUTE_TYPE_LABELS[type];
                        const candidates =
                          attributes?.[type]
                            ?.slice()
                            .sort((a, b) => a.localeCompare(b, 'sv')) ?? [];
                        const current =
                          decisions.attributeMappings?.[type]?.[
                            issue.importCode
                          ] ?? '';
                        return (
                          <Box
                            key={`trunc:${type}:${issue.importCode}:${issue.storageLimit}`}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              p: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 0.75 }}>
                              {label}: <b>{issue.importCode}</b> skulle kapas
                              till befintlig kod{' '}
                              <b>{issue.matchedExistingCode}</b> (gräns{' '}
                              {issue.storageLimit} tecken)
                            </Typography>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              alignItems={{ xs: 'stretch', sm: 'center' }}
                            >
                              <Autocomplete
                                options={candidates}
                                value={current || null}
                                disabled={loadingAttributes}
                                sx={{ flex: 1, minWidth: 220 }}
                                onChange={(_e, value) => {
                                  const next = value || '';
                                  setDecisions((prev) => ({
                                    ...prev,
                                    attributeMappings: {
                                      ...(prev.attributeMappings || {}),
                                      [type]: {
                                        ...(prev.attributeMappings?.[type] ||
                                          {}),
                                        [issue.importCode]: next,
                                      },
                                    },
                                  }));
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    label="Ersätt med"
                                    helperText="Välj vilken kod importen ska använda."
                                  />
                                )}
                              />
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setDecisions((prev) => ({
                                    ...prev,
                                    attributeMappings: {
                                      ...(prev.attributeMappings || {}),
                                      [type]: {
                                        ...(prev.attributeMappings?.[type] ||
                                          {}),
                                        [issue.importCode]:
                                          issue.matchedExistingCode,
                                      },
                                    },
                                  }));
                                }}
                              >
                                Använd {issue.matchedExistingCode}
                              </Button>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}

                {hasMissingAttributes ? (
                  <Alert severity="error">
                    Saknade färg/storlek/längd blockerar importen. Du kan
                    antingen lägga upp nya attribut i systemet, eller mappa
                    saknade koder till befintliga.
                  </Alert>
                ) : null}

                {hasMissingAttributes ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          Mappa saknade attribut
                        </Typography>
                        {loadingAttributes ? (
                          <Typography variant="caption" color="text.secondary">
                            Laddar attribut…
                          </Typography>
                        ) : null}
                      </Stack>

                      {validateResult?.blockingIssues.missingAttributes.map(
                        (m) => {
                          const type = m.attributeType as AttributeType;
                          const label = ATTRIBUTE_TYPE_LABELS[type];
                          const candidates =
                            attributes?.[type]
                              ?.slice()
                              .sort((a, b) => a.localeCompare(b, 'sv')) ?? [];

                          return (
                            <Box key={type}>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                {label}
                              </Typography>
                              <Stack spacing={1}>
                                {m.missing.map((missingCode) => {
                                  const current =
                                    decisions.attributeMappings?.[type]?.[
                                      missingCode
                                    ] ?? '';
                                  const suggestions = suggestSimilarCodes(
                                    missingCode,
                                    candidates,
                                  );
                                  return (
                                    <Box
                                      key={`${type}:${missingCode}`}
                                      sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        p: 1,
                                      }}
                                    >
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        alignItems={{
                                          xs: 'stretch',
                                          sm: 'center',
                                        }}
                                      >
                                        <Box sx={{ minWidth: 140 }}>
                                          <Typography variant="body2">
                                            Saknas: <b>{missingCode}</b>
                                          </Typography>
                                        </Box>

                                        <Autocomplete
                                          options={candidates}
                                          value={current || null}
                                          disabled={loadingAttributes}
                                          sx={{ flex: 1, minWidth: 220 }}
                                          onChange={(_e, value) => {
                                            const next = value || '';
                                            setDecisions((prev) => ({
                                              ...prev,
                                              attributeMappings: {
                                                ...(prev.attributeMappings ||
                                                  {}),
                                                [type]: {
                                                  ...(prev.attributeMappings?.[
                                                    type
                                                  ] || {}),
                                                  [missingCode]: next,
                                                },
                                              },
                                            }));
                                          }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              size="small"
                                              label="Ersätt med"
                                              helperText="Override: importen använder vald kod, inga nya attribut skapas."
                                            />
                                          )}
                                        />

                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={loadingAttributes}
                                          onClick={() =>
                                            setAttributeToAdd({
                                              attributeType: type,
                                              missingCode,
                                            })
                                          }
                                        >
                                          Lägg till ny
                                        </Button>

                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => {
                                            setDecisions((prev) => {
                                              const nextTypeMap = {
                                                ...(prev.attributeMappings?.[
                                                  type
                                                ] || {}),
                                              };
                                              delete nextTypeMap[missingCode];
                                              return {
                                                ...prev,
                                                attributeMappings: {
                                                  ...(prev.attributeMappings ||
                                                    {}),
                                                  [type]: nextTypeMap,
                                                },
                                              };
                                            });
                                          }}
                                        >
                                          Rensa
                                        </Button>
                                      </Stack>

                                      {suggestions.length ? (
                                        <Stack
                                          direction="row"
                                          spacing={0.75}
                                          flexWrap="wrap"
                                          useFlexGap
                                          sx={{ mt: 0.75 }}
                                        >
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mr: 0.5 }}
                                          >
                                            Liknande:
                                          </Typography>
                                          {suggestions.map((s) => (
                                            <Chip
                                              key={s}
                                              size="small"
                                              label={s}
                                              variant="outlined"
                                              onClick={() => {
                                                setDecisions((prev) => ({
                                                  ...prev,
                                                  attributeMappings: {
                                                    ...(prev.attributeMappings ||
                                                      {}),
                                                    [type]: {
                                                      ...(prev
                                                        .attributeMappings?.[
                                                        type
                                                      ] || {}),
                                                      [missingCode]: s,
                                                    },
                                                  },
                                                }));
                                              }}
                                            />
                                          ))}
                                        </Stack>
                                      ) : null}
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          );
                        },
                      )}
                    </Stack>
                  </Box>
                ) : null}

                {hasInconsistentEans ? (
                  <Alert severity="error">
                    Samma EAN förekommer flera gånger i filen men raderna har
                    olika artikel- eller variantdata (t.ex. artikelnummer, färg,
                    storlek eller längd). Det måste åtgärdas i filen innan
                    import kan göras.
                  </Alert>
                ) : null}

                {hasInconsistentEans ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Typography variant="subtitle2">
                      EAN med konflikter
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                      {inconsistentEanRows.map((i) => (
                        <Box key={i.ean13}>
                          <Typography variant="body2">EAN {i.ean13}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Rader: {i.rowIds.join(', ')}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                {hasStyleOverrideConflicts ? (
                  <Alert severity="error">
                    Vissa rader saknar artikelnummer och kan inte kopplas säkert
                    via style override (brand + style name) eftersom samma style
                    förekommer för flera artikelnummer i filen. Åtgärda filen
                    (gör style unik eller ange artikelnummer på raderna) och kör
                    kontroller igen.
                  </Alert>
                ) : null}

                {hasStyleOverrideConflicts ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Typography variant="subtitle2">
                      Style override-kollisioner
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                      {styleOverrideConflicts.map((c) => (
                        <Box key={c.styleKey}>
                          <Typography variant="body2">
                            Style: {c.styleKey}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Artikelnummer: {c.articleNumbers.join(', ')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Rader: {c.rowIds.join(', ')}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                {hasPluConflicts ? (
                  <Alert severity="error">
                    Importerade PLU innehåller konflikter. Åtgärda PLU-kolumnen
                    i filen eller stäng av PLU-import och kör kontroller igen.
                  </Alert>
                ) : null}

                {hasPluConflicts ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Typography variant="subtitle2">PLU-konflikter</Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                      {pluConflicts.map((c) => (
                        <Box key={`${c.reason}:${c.pluNo}`}>
                          <Typography variant="body2">PLU {c.pluNo}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c.reason === 'duplicate_in_import'
                              ? `Förekommer på flera rader i importen (${c.rowIds.join(', ')}).`
                              : `Används redan av artikel ${c.existingProductNumber ?? 'okänd'} i systemet.`}
                          </Typography>
                          {c.articleNumbers.length ? (
                            <Typography variant="body2" color="text.secondary">
                              Importerade artikelnummer:{' '}
                              {c.articleNumbers.join(', ')}
                            </Typography>
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                {hasBlockingIssues ? null : (
                  <>
                    {validateResult?.articleNumberConflicts.length ? (
                      <>
                        <Typography variant="h6" sx={{ mt: 1 }}>
                          Artikelnummer-konflikter
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Artikelnummer finns redan men kopplas till annan
                          leverantör. Välj hur du vill fortsätta.
                        </Typography>
                        <Stack spacing={1.5}>
                          {validateResult.articleNumberConflicts.map((c) => {
                            const resolution =
                              decisions.articleNumberResolutions?.[
                                c.articleNumber
                              ]?.action ?? '';
                            const prefixResolution =
                              decisions.articleNumberResolutions?.[
                                c.articleNumber
                              ];
                            const prefix =
                              prefixResolution?.action === 'prefix'
                                ? prefixResolution.prefix
                                : '';
                            return (
                              <Box
                                key={c.articleNumber}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                  p: 1.25,
                                }}
                              >
                                <Typography variant="subtitle2">
                                  {c.articleNumber}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Befintlig leverantör:{' '}
                                  {c.existingSupplierName ??
                                    c.existingSupplierId ??
                                    'Okänd'}
                                </Typography>
                                <FormControl sx={{ mt: 1 }}>
                                  <FormLabel>Åtgärd</FormLabel>
                                  <RadioGroup
                                    row
                                    value={resolution}
                                    onChange={(e) => {
                                      const action = e.target.value as
                                        | 'continue'
                                        | 'prefix';
                                      setDecisions((prev) => ({
                                        ...prev,
                                        articleNumberResolutions: {
                                          ...(prev.articleNumberResolutions ||
                                            {}),
                                          [c.articleNumber]:
                                            action === 'continue'
                                              ? { action: 'continue' }
                                              : {
                                                  action: 'prefix',
                                                  prefix: prefix || '',
                                                },
                                        },
                                      }));
                                    }}
                                  >
                                    <FormControlLabel
                                      value="continue"
                                      control={<Radio />}
                                      label="Fortsätt ändå"
                                    />
                                    <FormControlLabel
                                      value="prefix"
                                      control={<Radio />}
                                      label="Prefix"
                                    />
                                  </RadioGroup>
                                </FormControl>
                                {resolution === 'prefix' ? (
                                  <TextField
                                    size="small"
                                    sx={{ mt: 1, maxWidth: 320 }}
                                    label="Prefix"
                                    value={prefix}
                                    onChange={(e) => {
                                      const nextPrefix = e.target.value;
                                      setDecisions((prev) => ({
                                        ...prev,
                                        articleNumberResolutions: {
                                          ...(prev.articleNumberResolutions ||
                                            {}),
                                          [c.articleNumber]: {
                                            action: 'prefix',
                                            prefix: nextPrefix,
                                          },
                                        },
                                      }));
                                    }}
                                  />
                                ) : null}
                              </Box>
                            );
                          })}
                        </Stack>
                      </>
                    ) : null}

                    {validateResult?.eanMatches.length ? (
                      <>
                        <Typography variant="h6" sx={{ mt: 1 }}>
                          EAN-träffar
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Dessa EAN finns redan i systemet och kommer matchas
                          mot befintlig variant. Systemets PLU används även om
                          PLU importeras från Excel.
                        </Typography>
                        <Stack spacing={1}>
                          {sortedEanMatches.map((m) => (
                            <Accordion
                              key={m.rowId}
                              disableGutters
                              elevation={0}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                '&:before': { display: 'none' },
                                overflow: 'hidden',
                              }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={1}
                                  alignItems={{
                                    xs: 'flex-start',
                                    sm: 'center',
                                  }}
                                  sx={{ width: '100%' }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    <Chip
                                      size="small"
                                      color="success"
                                      label="Matchas"
                                    />
                                    <Typography variant="subtitle2">
                                      Rad {m.rowId}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      EAN {m.ean13}
                                    </Typography>
                                  </Stack>

                                  <Box sx={{ flex: 1 }} />

                                  <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    alignItems={{
                                      xs: 'flex-start',
                                      sm: 'center',
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {m.system.productNumber}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      PLU {m.system.pluNo ?? '—'}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 0 }}>
                                <Grid container spacing={1.5}>
                                  <Grid item xs={12} md={6}>
                                    <Box
                                      sx={{
                                        p: 1.25,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.hover',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Import (från Excel)
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 0.25 }}
                                      >
                                        {m.import.productName ?? '—'}
                                      </Typography>
                                      <Box sx={{ mt: 0.75 }}>
                                        <AttrChips
                                          a1={m.import.attribute1Code}
                                          a2={m.import.attribute2Code}
                                          a3={m.import.attribute3Code}
                                        />
                                      </Box>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Box
                                      sx={{
                                        p: 1.25,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.hover',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        System (befintlig variant)
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 0.25 }}
                                      >
                                        {m.system.productName ?? '—'}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.25 }}
                                      >
                                        Produkt {m.system.productNumber} • PLU{' '}
                                        {m.system.pluNo ?? '—'}
                                      </Typography>
                                      <Box sx={{ mt: 0.75 }}>
                                        <AttrChips
                                          a1={m.system.attribute1Code}
                                          a2={m.system.attribute2Code}
                                          a3={m.system.attribute3Code}
                                        />
                                      </Box>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Stack>
                      </>
                    ) : null}

                    {sortedVariantMatches.length ? (
                      <>
                        <Typography variant="h6" sx={{ mt: 1 }}>
                          Befintliga varianter med nytt EAN
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Systemets PLU används för samma produkt och variant.
                          EAN från importen skickas vidare vid
                          Viskan-överföring.
                        </Typography>
                        <Stack spacing={1}>
                          {sortedVariantMatches.map((m) => (
                            <Box
                              key={m.rowId}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                p: 1.25,
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                sx={{ mb: 1 }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <Chip
                                    size="small"
                                    color="info"
                                    label="Befintlig PLU"
                                  />
                                  <Typography variant="subtitle2">
                                    Rad {m.rowId}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Nytt EAN {m.ean13 ?? '—'}
                                  </Typography>
                                </Stack>
                                <Box sx={{ flex: 1 }} />
                                <Typography variant="body2">
                                  {m.system.productNumber} • PLU{' '}
                                  {m.system.pluNo ?? '—'}
                                </Typography>
                              </Stack>

                              <Grid container spacing={1.5}>
                                <Grid item xs={12} md={6}>
                                  <Box
                                    sx={{
                                      p: 1,
                                      borderRadius: 1.5,
                                      bgcolor: 'action.hover',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Import
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ mt: 0.25 }}
                                    >
                                      {m.import.productName ?? '—'} • PLU{' '}
                                      {m.import.pluNo ?? '—'}
                                    </Typography>
                                    <Box sx={{ mt: 0.75 }}>
                                      <AttrChips
                                        a1={m.import.attribute1Code}
                                        a2={m.import.attribute2Code}
                                        a3={m.import.attribute3Code}
                                      />
                                    </Box>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Box
                                    sx={{
                                      p: 1,
                                      borderRadius: 1.5,
                                      bgcolor: 'action.hover',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      System
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ mt: 0.25 }}
                                    >
                                      {m.system.productName ?? '—'} • PLU{' '}
                                      {m.system.pluNo ?? '—'}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.25 }}
                                    >
                                      Nuvarande EAN {m.system.ean13 ?? '—'}
                                    </Typography>
                                    <Box sx={{ mt: 0.75 }}>
                                      <AttrChips
                                        a1={m.system.attribute1Code}
                                        a2={m.system.attribute2Code}
                                        a3={m.system.attribute3Code}
                                      />
                                    </Box>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </Stack>
                      </>
                    ) : null}

                    {validateResult?.normalizedPreview?.length ? (
                      <>
                        <Typography variant="h6" sx={{ mt: 1 }}>
                          Preview (första raderna)
                        </Typography>
                        <Box
                          sx={{
                            maxHeight: 260,
                            overflow: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            p: 1,
                          }}
                        >
                          <Stack spacing={0.75}>
                            {validateResult.normalizedPreview.map((r) => {
                              const pluSourceLabel =
                                r.pluSource === 'excel'
                                  ? 'Excel'
                                  : r.pluSource === 'system'
                                    ? 'System'
                                    : null;
                              return (
                                <Box
                                  key={r.rowId}
                                  sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    pb: 0.75,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Rad {r.rowId}: {r.articleNumber ?? '—'} •{' '}
                                    {r.productName ?? '—'} •{' '}
                                    {r.attribute1Code ?? '—'}/
                                    {r.attribute2Code ?? '—'}/
                                    {r.attribute3Code ?? '—'} • EAN{' '}
                                    {r.ean13 ?? '—'} • PLU {r.pluNo ?? '—'}
                                    {pluSourceLabel
                                      ? ` (${pluSourceLabel})`
                                      : ''}{' '}
                                    • Vikt {r.weight ?? '—'} g
                                    {r.productMetadataLocked
                                      ? ' • Systemnamn låst'
                                      : ''}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Ord. {r.priceOrig ?? '—'} • Kampanj{' '}
                                    {r.campaignPrice ?? '—'} • DKK ord.{' '}
                                    {r.priceOrigDkk ?? '—'} • DKK pris{' '}
                                    {r.priceDkk ?? '—'} • Lev. ordernr{' '}
                                    {r.supplierOrderNumber ?? '—'} • Kommentar{' '}
                                    {r.notes ?? '—'}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      </>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} disabled={creating}>
            Stäng
          </Button>
          {missingProductsOnly ? (
            <Button
              variant="contained"
              onClick={handleExportMissingProducts}
              disabled={!canExportMissingProducts || creating}
            >
              Ladda ner saknade produkter ({missingProducts.length})
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!canCreate || creating}
            >
              {creating ? 'Skapar…' : 'Skapa inköpsorder'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <AddMissingAttributeDialog
        open={Boolean(attributeToAdd)}
        attributeType={attributeToAdd?.attributeType ?? null}
        missingCode={attributeToAdd?.missingCode ?? ''}
        onClose={() => setAttributeToAdd(null)}
        onAdd={(attributeCode, attributeNumber) => {
          if (!attributeToAdd) return Promise.resolve();
          return handleAddMissingAttribute(
            attributeToAdd.attributeType,
            attributeToAdd.missingCode,
            attributeCode,
            attributeNumber,
          );
        }}
      />
    </>
  );
}
